/**
 * Spinner - Loading spinner component.
 *
 * Features:
 * - Multiple sizes
 * - Customizable color
 * - Optional label text
 * - Fullscreen overlay variant
 */

import { forwardRef, type HTMLAttributes } from 'react';

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'white' | 'current';
  label?: string;
  fullscreen?: boolean;
}

const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  (
    {
      size = 'md',
      color = 'primary',
      label,
      fullscreen = false,
      className = '',
      ...props
    },
    ref
  ) => {
    // Size classes
    const sizeClasses = {
      sm: 'w-4 h-4 border-2',
      md: 'w-8 h-8 border-3',
      lg: 'w-12 h-12 border-4',
      xl: 'w-16 h-16 border-4',
    };

    // Color classes
    const colorClasses = {
      primary: 'border-white/10 border-t-accent',
      white: 'border-white/20 border-t-white',
      current: 'border-current/20 border-t-current',
    };

    const spinner = (
      <div
        ref={ref}
        className={`${fullscreen ? '' : 'inline-flex flex-col items-center gap-3'} ${className}`}
        {...props}
      >
        <div
          className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-spin`}
        />
        {label && (
          <span className="text-sm text-text-muted animate-pulse">
            {label}
          </span>
        )}
      </div>
    );

    // Fullscreen overlay variant
    if (fullscreen) {
      return (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-dark-bg/90 backdrop-blur-md">
          <div
            className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-spin`}
          />
          {label && (
            <span className="mt-4 text-base text-text-muted animate-pulse">
              {label}
            </span>
          )}
        </div>
      );
    }

    return spinner;
  }
);

Spinner.displayName = 'Spinner';

export default Spinner;
